import _ from 'lodash';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { decorate } from '@/trend/trendViewer/itemDecorator.utilities';
import { ITEM_DATA_STATUS, ITEM_TYPES, ITEM_TYPES_TO_API_TYPES } from '@/trendData/trendData.constants';
import { formulaIdentifierCollides } from '@/utilities/formulaVariables.utilities';
import { getMediumIdentifier, getShortIdentifier } from '@/utilities/utilities';
import type { FormulaParameter, FormulaToolParameterItem } from '@/tools/formula/formulaTool.types';

/** An item from the details pane, or an existing parameter's item, being considered as a formula variable. */
type CandidateItem = FormulaToolParameterItem & { name: string; itemType?: string; contextConditionId?: string };

/**
 * Get array of parameter names (without $ prepended) used in formula text
 */
export function getParamsInFormula(formula: string): string[] {
  let param;
  const paramRegEx = /\$(\w+)/g;
  const paramsInFormula: string[] = [];

  while ((param = paramRegEx.exec(formula)) !== null) {
    paramsInFormula.push(param[1]);
  }

  return paramsInFormula;
}

/**
 * Transforms the items in the details pane into an array of simplified name parameters. Keeps the supplied existing
 * parameters and skips any details pane items they already reference. Also skips the current formula.
 * Internally, it sorts the items by the length of their names before creating the simplified name in order to favor
 * keeping shorter names the same.
 *
 * @param existingParams - The formula's current parameters
 * @param formula - The formula's current text, whose `$` references are not reused as names
 * @param currentFormulaId - The id of the formula item being edited, if any
 */
export function getDetailsPaneParameters(
  existingParams: FormulaParameter[],
  formula: string,
  currentFormulaId: string | undefined,
): FormulaParameter[] {
  let paramLetterCount = 0;
  const existingParamNames = _.map(existingParams, 'identifier');

  return _.chain(
    getAllItems({
      excludeDataStatus: [ITEM_DATA_STATUS.REDACTED],
      // Types that can be used as parameters
      itemTypes: [ITEM_TYPES.SERIES, ITEM_TYPES.CONDITION, ITEM_TYPES.SCALAR],
    }),
  )
    .thru((items) => decorate({ items }) as CandidateItem[])
    .concat(_.map(existingParams, 'item') as CandidateItem[])
    .sort((a, b) => a.name.length - b.name.length)
    .map((item) => {
      let paramName;
      const existingParam = _.find(existingParams, ['item.id', item.id]);

      // Skip item if it is the current formula
      if (item.id === currentFormulaId) {
        return;
      }

      // If the parameter already exists, then use it
      if (existingParam) {
        return existingParam;
      }

      // Otherwise, choose a parameter name
      const paramsInFormula = getParamsInFormula(formula);

      // First try, use a "friendly" name based on the actual text of the item
      const namesToAvoid = _.concat(paramsInFormula, existingParamNames).map((name) => name.toLowerCase());

      paramName = getMediumIdentifier(item.name, namesToAvoid);
      if (_.isEmpty(paramName)) {
        // We couldn't come up with a simple identifier from the item name.
        // It may have been too long, or all numbers and symbols.
        // Fall back the old naming scheme for parameters as $a, $b, $c.
        do {
          paramName = getShortIdentifier(paramLetterCount++);
        } while (_.includes(namesToAvoid, paramName));
      }

      // Remember this name so we don't use it again on a later parameter
      existingParamNames.push(paramName);

      // Get API type from item type
      item.type = ITEM_TYPES_TO_API_TYPES[item.itemType as string];
      return {
        identifier: paramName,
        item: _.pick(item, ['id', 'name', 'assets', 'type', 'contextConditionId']),
      };
    })
    .orderBy(['item.name'], ['asc'])
    .compact()
    .uniq()
    .value() as FormulaParameter[];
}

/**
 * Checks replacing `originalParameter` with `parameter`, e.g. when the user renames a variable.
 *
 * @returns The translation key of the problem, or `undefined` if the update is valid
 */
export function getParameterUpdateError(
  parameters: FormulaParameter[],
  parameter: FormulaParameter,
  originalParameter: FormulaParameter,
): string | undefined {
  if (!parameter.identifier.trim()) {
    return 'INVALID_NAME';
  }

  const isUnique = !formulaIdentifierCollides(
    _.map(parameters, 'identifier'),
    parameter.identifier,
    originalParameter.identifier,
  );
  if (!isUnique) {
    return 'FORMULA.VARIABLE_UNIQUE';
  }

  return undefined;
}
